import { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/shared/utils';

interface Props {
  messages: string[];
  intervalMs?: number;
  onComplete: () => void;
}

type RowState = 'pending' | 'loading' | 'done';

export function StepProgressLoader({ messages, intervalMs = 900, onComplete }: Props) {
  const [rows, setRows] = useState<RowState[]>(messages.map(() => 'pending'));
  const completedRef = useRef(false);

  useEffect(() => {
    let current = 0;
    completedRef.current = false;

    const tick = () => {
      if (current >= messages.length) {
        if (!completedRef.current) {
          completedRef.current = true;
          setTimeout(onComplete, 400);
        }
        return;
      }
      // Mark current row as loading
      setRows((prev) => {
        const next = [...prev];
        next[current] = 'loading';
        return next;
      });
      setTimeout(() => {
        // Mark current row as done, move to next
        setRows((prev) => {
          const next = [...prev];
          next[current] = 'done';
          return next;
        });
        current++;
        setTimeout(tick, 120);
      }, intervalMs);
    };

    // small delay before starting
    const t = setTimeout(tick, 200);
    return () => {
      clearTimeout(t);
      completedRef.current = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex flex-col gap-2.5">
      {messages.map((msg, i) => {
        const state = rows[i];
        return (
          <div
            key={i}
            className={cn(
              'flex items-center gap-3 transition-all duration-300',
              state === 'pending' ? 'opacity-0 translate-y-1' : 'opacity-100 translate-y-0',
            )}
          >
            <div className="flex size-5 shrink-0 items-center justify-center">
              {state === 'loading' && (
                <div className="size-4 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
              )}
              {state === 'done' && (
                <div className="flex size-5 items-center justify-center rounded-full bg-primary">
                  <Check className="size-3 text-primary-foreground" strokeWidth={2.5} />
                </div>
              )}
              {state === 'pending' && (
                <div className="size-4 rounded-full border-2 border-border" />
              )}
            </div>
            <span
              className={cn(
                'text-sm transition-colors',
                state === 'done' ? 'text-foreground' : 'text-muted-foreground',
                state === 'loading' && 'text-foreground font-medium',
              )}
            >
              {msg}
            </span>
          </div>
        );
      })}
    </div>
  );
}
